import { z } from 'zod';

import { pageQuery } from './envelope.ts';

/**
 * §16 Risk — the case file's request shapes.
 *
 * `risk.risk_case` existed as a TABLE and nothing else: five columns, one
 * seeded row, and not one route in `services/api` that created, added to or
 * closed one. The ESCALATED verdict on an alert — which the console has offered
 * since it was built — wrote a word into an audit row and led nowhere. A case
 * that cannot be opened, evidenced or resolved is a screen, not a case.
 *
 * WHY THESE ARE HERE AND THE RULE REGISTRY IS NOT. This file describes what a
 * REQUEST may contain, which is what `packages/contracts` is for. Which rules
 * the platform can actually evaluate is a statement about the SCHEMA — two of
 * the four shipped rules key on tables that do not exist — so it lives in
 * `@dayon/db` (`packages/db/src/risk-rules.ts`), where both the worker that
 * evaluates and the API that reports can read the one copy. The worker cannot
 * import this package at all: it does not depend on it, and adding the
 * dependency to have two copies of the same list would be the drift the single
 * copy exists to prevent.
 */

/**
 * How a case ended.
 *
 * INCONCLUSIVE is in the set on purpose. Without it an investigator who could
 * not settle a case has to choose between CONFIRMED and CLEARED, and they will
 * choose CLEARED — which turns the cleared count, the one number a compliance
 * review reads, into a mixture of "we checked and it was fine" and "we could
 * not tell". OPEN is not offered: it is the state a case starts in, not a
 * verdict anybody reached.
 *
 * The same four words — these three plus OPEN — are the CHECK constraint
 * `risk_case_verdict` in `packages/db/sql/240_risk_evaluation.sql`, so a direct
 * UPDATE cannot write a verdict this set does not contain either.
 */
export const riskCaseVerdict = z.enum(['CONFIRMED', 'CLEARED', 'INCONCLUSIVE']);
export type RiskCaseVerdict = z.infer<typeof riskCaseVerdict>;

/**
 * A written finding.
 *
 * Ten characters, matching the CHECK on `risk.risk_case_note`. The floor is not
 * validation theatre: a finding is the only evidence this platform can hold,
 * and "looks bad" is not one.
 *
 * THERE IS NO ATTACHMENT FIELD, and there is no attachment table behind this.
 * There is no object store anywhere in this workspace, so a case takes written
 * findings and nothing else. Saying so is the honest half; a field that
 * accepted a file name nobody could open would be the dishonest one.
 */
export const riskCaseFindingRequest = z.strictObject({
  body: z
    .string()
    .min(10, 'Write what you found — at least 10 characters. Somebody else reads this case.')
    .max(4000),
});
export type RiskCaseFindingRequest = z.infer<typeof riskCaseFindingRequest>;

/** Closing a case: a verdict, and the sentence it was reached on. */
export const resolveRiskCaseRequest = z.strictObject({
  verdict: riskCaseVerdict,
  note: z.string().min(10, 'Say what the verdict rests on — at least 10 characters.').max(4000),
});
export type ResolveRiskCaseRequest = z.infer<typeof resolveRiskCaseRequest>;

/**
 * Opening a case by hand.
 *
 * The usual path is automatic: closing an alert as ESCALATED opens the case in
 * the same transaction as the verdict, so an escalation finally lands somewhere
 * instead of being a word in an audit row. This is the other path — an
 * investigator opening a case on a player with no alert to escalate, which is
 * how an enquiry from a bank or a colleague's tip-off gets recorded.
 */
export const openRiskCaseRequest = z.strictObject({
  playerId: z.string().min(1),
  summary: z.string().min(10, 'Say what the case is about — at least 10 characters.').max(1000),
});
export type OpenRiskCaseRequest = z.infer<typeof openRiskCaseRequest>;

/* ── The alert queue, paged ─────────────────────────────────────────────── */

/**
 * The four states an alert can be in.
 *
 * The same four the console's chip row offers and the same four
 * `risk_alert.status` holds. Written here as a closed set because the query
 * below puts the value into a WHERE clause: an open string would be a filter
 * nobody can predict the result of, and a typo would silently return an empty
 * queue on the one screen where "no alerts" is a conclusion somebody acts on.
 */
export const riskAlertStatus = z.enum(['OPEN', 'CLEARED', 'RESTRICTED', 'ESCALATED']);
export type RiskAlertStatus = z.infer<typeof riskAlertStatus>;

/**
 * WHY THIS EXISTS: the queue could not reach its own contents.
 *
 * `GET /api/risk/alerts` took a brand and nothing else, and ended in a bare
 * `LIMIT 100`. The development database holds 456 OPEN alerts, so 356 of them
 * could not be reached from the console at all — not by scrolling, not by
 * searching, not by any sequence of clicks. The screen was honest about the cap
 * and that is the only reason this was a capacity limit rather than a lie.
 *
 * SEARCH AND STATUS MOVE TO THE SERVER WITH THE PAGING, and they have to.
 * Both ran client-side over whatever had been loaded. Paging alone would have
 * narrowed that window from 100 rows to 25 and made searching strictly worse —
 * an operator pasting a reference from a chat message would be told there is no
 * such alert because it was on page 7.
 */
export const riskAlertQuery = pageQuery.extend({
  brandId: z.string().min(1).optional(),
  /** Absent means every status — the "All" chip. */
  status: riskAlertStatus.optional(),
  /** Matched against the alert ref, the player's name and ref, and the rule. */
  q: z.string().trim().min(1).max(120).optional(),
});
export type RiskAlertQuery = z.infer<typeof riskAlertQuery>;

/**
 * The figures beside the queue, counted over the WHOLE filtered set.
 *
 * Not derivable from a page, which is exactly why they are here. The console
 * computed all of these from the loaded array — the open count, the critical
 * count, every chip badge, and the held-withdrawal tally. Against a full queue
 * of 100 that was near enough; against a page of 25 each one becomes a
 * different, smaller and quietly wrong number, and this screen's entire job is
 * deciding whether a figure can be trusted.
 *
 * `byStatus` is counted over the SEARCH result and ignores the status filter,
 * because a chip badge is a promise about what that chip will produce. The
 * others are counted over the brand scope alone: they describe the queue, not
 * the operator's current narrowing of it.
 */
export const riskAlertSummary = z.object({
  /** Open alerts in the brand scope, whatever is searched or filtered. */
  open: z.number().int().nonnegative(),
  /** Of those, CRITICAL. */
  criticalOpen: z.number().int().nonnegative(),
  /** Chip badges: how many the chip would produce, per status. */
  byStatus: z.record(riskAlertStatus, z.number().int().nonnegative()),
  /** Withdrawals held by these alerts, and how many players they belong to. */
  heldWithdrawals: z.number().int().nonnegative(),
  heldPlayers: z.number().int().nonnegative(),
});
export type RiskAlertSummary = z.infer<typeof riskAlertSummary>;
